import type { PublicForm } from './types';

const BASE_URL = import.meta.env.VITE_ENGAGE_PUBLIC_API_URL ?? '';

/**
 * A failed request, carrying the API's machine-readable `error.code`.
 *
 * The code matters more than the message here: the platform sanitises 5xx
 * messages in production (`resolveClientErrorMessage` in
 * shared/backend-platform), so "Direct Debit is not available right now" is the
 * text a developer sees and something generic is what a supporter gets. The
 * code survives that, which makes it the only reliable thing to branch on.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

async function handleResponse<T>(res: Response): Promise<T> {
  const body = await res.json();
  if (!res.ok) {
    throw new ApiError(body?.error?.message ?? `Request failed: ${res.status}`, res.status, body?.error?.code);
  }
  return body.data as T;
}

function withTimeout(ms: number): AbortSignal {
  return AbortSignal.timeout(ms);
}

export async function getPublicForm(tenantSlug: string, formId: string): Promise<PublicForm> {
  const res = await fetch(`${BASE_URL}/public/${encodeURIComponent(tenantSlug)}/forms/${encodeURIComponent(formId)}`, {
    signal: withTimeout(10_000),
  });
  return handleResponse<PublicForm>(res);
}

export interface PaymentIntentResult {
  clientSecret: string;
  publishableKey: string;
  stripeAccountId: string;
  submissionId: string;
}

export interface PaymentIntentInput {
  values: Record<string, unknown>;
  amountMinor?: number;
  frequency?: 'one_off' | 'recurring';
}

export async function createPaymentIntent(
  tenantSlug: string,
  formId: string,
  embedKey: string,
  input: PaymentIntentInput,
): Promise<PaymentIntentResult> {
  const res = await fetch(`${BASE_URL}/public/${encodeURIComponent(tenantSlug)}/forms/${encodeURIComponent(formId)}/payment-intent`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Embed-Key': embedKey },
    body: JSON.stringify(input),
    signal: withTimeout(15_000),
  });
  return handleResponse<PaymentIntentResult>(res);
}

export interface DirectDebitFlowResult {
  /** GoCardless-hosted page where the supporter authorises the mandate. */
  authorisationUrl: string;
  billingRequestId: string;
  submissionId: string;
}

/**
 * Start a Direct Debit sign-up. The submission is stored, and the supporter's
 * Contact dispatched, server-side before this returns, so the supporter can
 * safely be sent away to `authorisationUrl` — nothing is lost if their browser
 * never comes back. Whether they went on to authorise the mandate is recorded
 * on the Contact's timeline, not on the submission.
 *
 * Unlike the endpoints above, this one still resolves its tenant from the
 * request host rather than a `tenantSlug` path segment — a known gap tracked
 * separately, out of scope here (see #1439).
 */
export async function createDirectDebitFlow(
  formId: string,
  embedKey: string,
  input: PaymentIntentInput,
): Promise<DirectDebitFlowResult> {
  const res = await fetch(`${BASE_URL}/public/forms/${formId}/direct-debit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Embed-Key': embedKey },
    body: JSON.stringify(input),
    signal: withTimeout(15_000),
  });
  return handleResponse<DirectDebitFlowResult>(res);
}

export async function submitForm(
  tenantSlug: string,
  formId: string,
  embedKey: string,
  values: Record<string, unknown>,
): Promise<void> {
  const res = await fetch(`${BASE_URL}/public/${encodeURIComponent(tenantSlug)}/forms/${encodeURIComponent(formId)}/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Embed-Key': embedKey },
    body: JSON.stringify(values),
    signal: withTimeout(15_000),
  });
  await handleResponse<void>(res);
}
